// Host-only YouTube IFrame player. Guests must not mount this component.
import React, { useEffect, useRef } from 'react';
import usePlayerStore from '../stores/playerStore';
import { useRoleStore } from '../stores/roleStore';
import { isEventHost } from '../utils/identity';
import { stopPlayerHeartbeat } from '../services/sessionSync';
import { applyHostYouTubeReady, resolveYouTubePlayingState } from '../utils/playbackClock';
import { clearPendingHostSeek, isHostSeekPending, tryConfirmHostSeek } from '../utils/hostSeek';

function isHostNow() {
  const { userId, hostId } = useRoleStore.getState();
  return isEventHost(userId, hostId);
}

const YouTubePlayer = ({ videoId, onStateChange, onError }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const frameRef = useRef(null);
  const isPlayerReadyRef = useRef(false);

  const {
    setDuration,
    setCurrentTime,
    volume,
    isPlaying,
    setPlayerInstance,
    setIsPlaying,
    handleEnded,
  } = usePlayerStore();

  const setPlayerInstanceRef = useRef(setPlayerInstance);
  const setDurationRef = useRef(setDuration);
  const setCurrentTimeRef = useRef(setCurrentTime);
  const setIsPlayingRef = useRef(setIsPlaying);
  const handleEndedRef = useRef(handleEnded);

  useEffect(() => {
    setPlayerInstanceRef.current = setPlayerInstance;
    setDurationRef.current = setDuration;
    setCurrentTimeRef.current = setCurrentTime;
    setIsPlayingRef.current = setIsPlaying;
    handleEndedRef.current = handleEnded;
  }, [setPlayerInstance, setDuration, setCurrentTime, setIsPlaying, handleEnded]);

  const safePlayerCall = (methodName, ...args) => {
    const player = playerRef.current;
    if (player && isPlayerReadyRef.current && typeof player[methodName] === 'function') {
      try {
        return player[methodName](...args);
      } catch (err) {
        console.warn(`Failed to call ${methodName}:`, err);
      }
    }
    return null;
  };

  useEffect(() => {
    if (!isHostNow()) return undefined;

    let isMounted = true;

    const createPlayer = () => {
      if (!containerRef.current || playerRef.current || !isMounted) return;

      playerRef.current = new window.YT.Player(containerRef.current, {
        height: '0',
        width: '0',
        videoId,
        playerVars: {
          autoplay: usePlayerStore.getState().isPlaying ? 1 : 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          playsinline: 1
        },
        events: {
          onReady: (event) => {
            if (!isMounted) return;
            isPlayerReadyRef.current = true;

            try {
              event.target.setVolume(volume);
              setPlayerInstanceRef.current(event.target);

              const dur = event.target.getDuration?.();
              if (dur) setDurationRef.current(dur);

              const recovered = usePlayerStore.getState();
              applyHostYouTubeReady(event.target, {
                currentTime: recovered.currentTime,
                isPlaying: recovered.isPlaying,
              });
            } catch (err) {
              console.warn('Error initializing player:', err);
            }
          },
          onStateChange: async (event) => {
            if (!isMounted) return;
            onStateChange?.(event);

            const PlayerState = window.YT?.PlayerState || {};
            if (event.data === PlayerState.ENDED) {
              await handleEndedRef.current();
              return;
            }

            if (isHostSeekPending()) {
              let actualTime = null;
              try {
                const time = event.target.getCurrentTime?.();
                if (typeof time === 'number' && Number.isFinite(time)) {
                  actualTime = time;
                }
              } catch {
                actualTime = null;
              }
              tryConfirmHostSeek({
                ready: isPlayerReadyRef.current,
                ytState: event.data,
                actualTime,
              });
              return;
            }

            // UNSTARTED and CUED are reported alongside PAUSED so the store can
            // never claim playback while the iframe is idle, which is what a
            // blocked autoplay leaves behind on host rejoin. Only a real change
            // is forwarded, so a mid-track reload does not publish a pause the
            // following PLAYING would immediately undo.
            const playing = resolveYouTubePlayingState(event.data);
            if (playing !== null && playing !== usePlayerStore.getState().isPlaying) {
              setIsPlayingRef.current(playing);
            }
          },
          onError: (event) => {
            if (!isMounted) return;
            console.error('YouTube player error:', event);
            onError?.(event);
          }
        }
      });
    };

    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = createPlayer;
    } else {
      createPlayer();
    }

    return () => {
      isMounted = false;
      isPlayerReadyRef.current = false;
      clearPendingHostSeek();
      stopPlayerHeartbeat();

      if (frameRef.current) cancelAnimationFrame(frameRef.current);

      try {
        playerRef.current?.destroy();
      } catch (err) {
        console.warn('Error destroying player:', err);
      }
      playerRef.current = null;
    };
    // Player is created once; later video/volume/state updates use dedicated effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateTime = () => {
      if (isPlayerReadyRef.current && isPlaying) {
        const time = safePlayerCall('getCurrentTime');
        if (time != null) setCurrentTimeRef.current(time);
        frameRef.current = requestAnimationFrame(updateTime);
      }
    };

    if (isPlaying) {
      frameRef.current = requestAnimationFrame(updateTime);
    } else if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [isPlaying]);

  useEffect(() => {
    if (isPlayerReadyRef.current) safePlayerCall('setVolume', volume);
  }, [volume]);

  useEffect(() => {
    if (isPlayerReadyRef.current && videoId) {
      safePlayerCall('loadVideoById', videoId);
    }
  }, [videoId]);

  return <div ref={containerRef} />;
};

export default YouTubePlayer;
